import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { isEmpty } from 'lodash';
import BaseTable, { SortOrder } from 'react-base-table';
import { useSelector, useDispatch } from 'react-redux';
import { useAtomValue } from 'jotai';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import { filterSelector } from '@store/common/selectors';
import { Retry } from '@components/common';
import selector from '@store/appViewerSessions/selectors';
import { createFetchData } from '@store/appViewerSessions/actions';
import SearchTool from './SearchTool';
import CustomCell from './CustomCell';
import Empty from './Empty';
import { selectedMetricsAtom, anchorMetricsAtom, allMetricsAtom } from '../atoms';
import { UserSessionsTableWrapper, UserSessionsTableHeader } from './styled';
import { getSegregatedMetrics, getSortAndSearchTable } from '../utils';
import userSessionExport from './userSessionExport';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { useGlobalFilterUUID } from './CustomCell/useGlobalFilterUUID';
import { DeletedMetric } from '@components/common/DeletedMetric';
import { errorDataAtom } from '@src/components/metrics-panel-hz/atoms/index';
import { getMetricMetaByKey, isSecondaryOnlyMetric } from '@src/utils/metrics/metricHelpers';
import { useCurrentFlowConfig } from '@src/hooks/flow/useFlowConfigs';
import { appendFlowIdentifier } from '@src/utils/filter';

const UserSessionsTable = ({ interval }) => {
  const dispatch = useDispatch();
  const { response, isFetching, errorCode, errorMsg } = useSelector(selector);
  const allMetrics = useAtomValue(allMetricsAtom);
  const anchoredMetrics = useAtomValue(anchorMetricsAtom);
  const selectedMetrics: any = useAtomValue(selectedMetricsAtom);
  const metricFilter = useSelector(metricFilterRulesSelector);
  const globalFilter = useSelector(filterSelector);
  const selectedMetricCardMetric: any = useSelector(selectedMetricSelector);
  const [tableColumns, setTableColumns] = useState([]);
  const [formattedMetricList, setFormattedMetricList] = useState({});
  const [tableData, setTableData] = useState([]);
  const { dataScope, usecaseKey, eventId }: any = useSelector(activeDashboardSelector);
  const errorData = useAtomValue(errorDataAtom);
  const flowConfig = useCurrentFlowConfig();

  const sortBy = useMemo(
    () => [
      {
        metric: selectedMetricCardMetric,
        order: 'desc',
      },
    ],
    [selectedMetricCardMetric],
  );

  const [columnSearchData, setColumnSearchData] = useState({
    text: '',
    key: '',
  });

  const [columnSortData, setColumnSortData] = useState<{
    order: SortOrder;
    key: string;
  }>({
    order: 'desc',
    key: selectedMetricCardMetric,
  });

  const onSearch = useCallback((data) => {
    setColumnSearchData(data);
  }, []);

  useEffect(() => {
    setColumnSortData({
      order: 'desc',
      key: selectedMetricCardMetric,
    });
  }, [selectedMetricCardMetric]);

  useEffect(() => {
    if (!isEmpty(allMetrics)) {
      const allMetricList = {};
      Object.keys(allMetrics).map((key) => {
        allMetrics[key].map((item) => {
          allMetricList[item.metric] = item;
        });
      });
      setFormattedMetricList(allMetricList);
    }
  }, [allMetrics]);

  useEffect(() => {
    if (!selectedMetrics.includes(columnSortData.key)) {
      setColumnSortData({
        order: 'desc',
        key: selectedMetricCardMetric,
      });
      setColumnSearchData({
        text: '',
        key: '',
      });
    }
  }, [selectedMetrics]);

  useEffect(() => {
    const formattedTableData = getSortAndSearchTable(
      response,
      columnSearchData,
      columnSortData,
      selectedMetrics,
    );
    setTableData(formattedTableData);
  }, [response, columnSortData, columnSearchData, selectedMetrics]);

  const segregatedMetrics = useMemo(
    () => getSegregatedMetrics(selectedMetrics, formattedMetricList),
    [formattedMetricList, selectedMetrics],
  );

  const secondaryOnlyMetrics = useMemo(
    () =>
      segregatedMetrics.otherMetrics.filter((metric) =>
        isSecondaryOnlyMetric({ metricKey: metric }),
      ),
    [segregatedMetrics.otherMetrics],
  );

  const invalidMetrics = useMemo(() => {
    return segregatedMetrics.otherMetrics.filter((metric) => !formattedMetricList[metric]);
  }, [segregatedMetrics, formattedMetricList]);

  const fetchData = useCallback(() => {
    if (!isEmpty(formattedMetricList)) {
      const dimensions = [...segregatedMetrics.dimensions];
      const filter = flowConfig?.identifier
        ? appendFlowIdentifier(globalFilter, flowConfig.identifier)
        : globalFilter;

      dispatch(
        createFetchData({
          interval,
          metrics: [
            ...new Set([...segregatedMetrics.metrics, ...invalidMetrics, ...secondaryOnlyMetrics]),
          ],
          metricFilter,
          filter,
          dimensions,
          sortBy,
          dataScope,
          presetKey: usecaseKey,
          eventId,
        }),
      );
    }
  }, [
    dataScope,
    usecaseKey,
    dispatch,
    formattedMetricList,
    globalFilter,
    interval,
    metricFilter,
    secondaryOnlyMetrics,
    segregatedMetrics.dimensions,
    segregatedMetrics.metrics,
    sortBy,
    invalidMetrics,
    flowConfig,
    eventId,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  userSessionExport({ data: tableData, columns: tableColumns, interval });

  const headerRender = useCallback(
    ({ cells, columns: tableColumns }) =>
      tableColumns.map((item, index) => {
        return (
          <UserSessionsTableHeader key={item.title + index} title={item.title}>
            {cells[index]}
            <SearchTool
              currentKey={item.dataKey}
              columnSearchData={columnSearchData}
              onSearch={onSearch}
            />
          </UserSessionsTableHeader>
        );
      }),
    [columnSearchData, onSearch],
  );

  const columnSort = (data) => {
    const { key, order } = data;
    setColumnSortData({
      order,
      key,
    });
  };

  useEffect(() => {
    if (!isEmpty(formattedMetricList)) {
      const generateNewColumns = (metricArray) =>
        metricArray.map((item) => {
          if (secondaryOnlyMetrics.includes(item)) {
            const meta = getMetricMetaByKey({ metricKey: item });
            return {
              dataKey: item,
              frozen: false,
              title: meta.displayName ? meta.displayName : '',
              unit: meta.unit ? meta.unit || 'unitless' : 'unitless',
              width: 200,
              height: 60,
              resizable: true,
              sortable: true,
              maxWidth: 340,
              minWidth: 60,
              key: item,
            };
          }
          return {
            dataKey: item,
            frozen: anchoredMetrics[item] ? 'left' : false,
            title: errorData[item] ? (
              <DeletedMetric />
            ) : formattedMetricList[item] ? (
              formattedMetricList[item].name
            ) : (
              ''
            ),
            unit: formattedMetricList[item]
              ? formattedMetricList[item].unit || 'unitless'
              : 'unitless',
            width: 200,
            height: 60,
            resizable: true,
            sortable: errorData[item] ? false : true,
            maxWidth: 340,
            minWidth: 60,
            key: item,
          };
        });
      const columnsData = generateNewColumns(selectedMetrics);
      setTableColumns(columnsData);
    }
  }, [anchoredMetrics, selectedMetrics, formattedMetricList, secondaryOnlyMetrics, errorData]);

  useGlobalFilterUUID();

  const TableCell = useCallback(
    (props) => <CustomCell {...props} flowId={flowConfig?.id} />,
    [flowConfig?.id],
  );

  return (
    <UserSessionsTableWrapper>
      <Retry
        isLoading={isFetching}
        showError={errorCode !== 0}
        errorMessage={errorMsg}
        fetchData={fetchData}
        progress={true}
      >
        <AutoSizer key="viewer-sessions-table">
          {({ width, height }) => (
            <BaseTable
              data={tableData}
              width={width}
              height={height}
              columns={tableColumns}
              fixed
              headerRenderer={headerRender}
              sortBy={columnSortData}
              onColumnSort={columnSort}
              components={{
                TableCell,
              }}
              emptyRenderer={<Empty />}
            />
          )}
        </AutoSizer>
      </Retry>
    </UserSessionsTableWrapper>
  );
};

export default UserSessionsTable;
